import { get, post } from "@libs:axios";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { CamelCasedPropertiesDeep } from "type-fest/source/camel-cased-properties-deep";

import {
  AlignedSegment,
  DiarizedSegment,
  DiarizedWordSegment,
  ExportFormat,
  GetTranscriptionApiAudioBotTaskIdGetData,
  SingleWordSegment,
  TranscribeApiAudioPostData,
  TranscriptExportRequest,
  TranscriptionResultSchema,
  TranscriptionSegment,
  TranscriptionStartedSchema,
  TranscriptionStatusSchema,
} from "@:openapi";

export enum TranscriptionStage {
  Transcription = "transcription",
  Alignment = "alignment",
  Diarization = "diarization",
}

export interface Audio {
  POST: {
    RequestQuery: CamelCasedPropertiesDeep<TranscribeApiAudioPostData["query"]>;
    // What callers hand to the mutation: the file and its options (the bot is given to the hook).
    Variables: { file: File } & Omit<
      CamelCasedPropertiesDeep<TranscribeApiAudioPostData["query"]>,
      "botId"
    >;
    Response: CamelCasedPropertiesDeep<TranscriptionStartedSchema>;
  };
  GET: {
    RequestParams: CamelCasedPropertiesDeep<
      GetTranscriptionApiAudioBotTaskIdGetData["path"]
    >;
    // Its result once completed, otherwise the state it is in.
    Response: CamelCasedPropertiesDeep<TranscriptionResultSchema>;
    State: CamelCasedPropertiesDeep<TranscriptionStatusSchema>;
    // One segment of any stage; only diarized segments carry a speaker.
    Segment: CamelCasedPropertiesDeep<
      TranscriptionSegment | AlignedSegment | DiarizedSegment
    >;
    Word: CamelCasedPropertiesDeep<SingleWordSegment | DiarizedWordSegment>;
  };
}

export const audioQueryKeys = createQueryKeys("audio", {
  get: (params: Audio["GET"]["RequestParams"] & { stage?: string }) => [params],
});

/*
 * The file is sent as the raw request body, typed by its Content-Type,
 * so that the server streams it to disk instead of parsing multipart.
 */
export const useMutationTranscribeAudio = (
  botId: string,
  options?: UseMutationOptions<
    Audio["POST"]["Response"],
    AxiosError,
    Audio["POST"]["Variables"]
  >,
) => {
  return useMutation({
    mutationFn: ({
      file,
      languageCode,
      numSpeakers,
    }: Audio["POST"]["Variables"]) =>
      post<Audio["POST"]["Response"]>("/api/audio", file, {
        params: {
          botId,
          languageCode: languageCode || undefined,
          numSpeakers: numSpeakers || undefined,
        } satisfies Audio["POST"]["RequestQuery"],
        headers: { "Content-Type": file.type },
      }),
    ...options,
  });
};

/*
 * A transcription answers:
 *  - 200 with its result if completed successfully
 *  - 202 with its state if still running
 *  - 424 with the error it failed with (if failed)
 *
 * Without a `stage`, the most advanced result available is returned: diarization > alignment > transcription.
 * A stage the bot is not configured to produce answers 404, leaving the query without data.
 */
export const useQueryTranscription = (
  botTaskId: string,
  stage?: TranscriptionStage,
  options?: Omit<
    UseQueryOptions<Audio["GET"]["Response"], AxiosError>,
    "queryKey"
  >,
) => {
  return useQuery({
    queryKey: audioQueryKeys.get({ botTaskId, stage }).queryKey,
    queryFn: () => {
      return get<Audio["GET"]["Response"]>(
        `/api/audio/${botTaskId}`,
        stage ? { stage } : undefined,
      );
    },
    ...options,
  });
};

/*
 * The state of a transcription that answered 424 (failed),
 * which axios treats as an error rather than as data.
 */
export const transcriptionStateFromError = (
  error: unknown,
): Audio["GET"]["State"] | undefined => {
  const state = (error as AxiosError)?.response?.data as
    | Audio["GET"]["State"]
    | undefined;

  return state?.status ? state : undefined;
};

/*
 * A transcription that is not completed answers with its state instead of a result,
 * and axios hands over the body without its status code.
 */
export const isTranscriptionState = (
  body: Audio["GET"]["Response"] | Audio["GET"]["State"] | undefined,
): body is Audio["GET"]["State"] =>
  !!body && "status" in body && !("result" in body);

export type { ExportFormat, TranscriptExportRequest };

/*
 * Exports the transcript and its summary as a Word (docx) or PDF document.
 */
export const useMutationExportTranscript = (
  botId: string,
  format: ExportFormat,
  options?: UseMutationOptions<Blob, AxiosError, TranscriptExportRequest>,
) => {
  return useMutation({
    mutationFn: (data: TranscriptExportRequest) =>
      post<Blob>("/api/audio/export", data, {
        params: { botId, format },
        responseType: "blob",
      }),
    ...options,
  });
};
